import React, { useState, useEffect, useCallback, useRef } from 'react'
import debounce from 'lodash/debounce'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import {
   Grid,
   Button,
   FormControl,
   FormControlLabel,
   FormHelperText,
   Typography,
   Checkbox,
   TextField,
   CircularProgress
} from '@material-ui/core'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'

import { useForm } from 'utils/customHooks/useForm'
import TRANSLATE, { OPERACIONES_GRUPOS } from 'utils/constants'
import { getFilteredUSersApi } from 'actions/userActions'
import { getAllActiveGroupsApi } from 'actions/groupActions'
import { useStyles } from './Form.styles'
import { useHasOperation } from 'hooks/useHasOperation'
import UTILS from './Form.utils'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" style={{ color: '#A6A6A6' }} />
const checkedIcon = <CheckBoxIcon fontSize="small" style={{ color: '#12B3F9' }} />
const DELAY_REQUEST = 1000

const filterOptions = createFilterOptions({
   stringify: option => option.name + option.lastName + option.email
})

let renders = 1

const GroupToRolesAndUsersForm = ({ data, setData, assignGroupToRolesAndUsers }) => {
   const classes = useStyles()

   const [formData, _, setForm, reset] = useForm(data)

   const [isInputError, setIsInputError] = useState({
      groupId: false,
      usersId: ''
   })

   const [userListFiltered, setUserListFiltered] = useState([])
   const [userToSearch, setUserToSearch] = useState('')
   const [groups, setGroups] = useState([])
   const [groupsQuery, setGroupsQuery] = useState(null)
   const [areGroupsLoading, setGroupsStatus] = useState(false)
   const [requestLoading, setRequestLoading] = useState(false)

   const RELACIONAR_GRUPO = useHasOperation(OPERACIONES_GRUPOS.RELACIONAR_GRUPOS_ROLES_USUARIOS)

   const isMassiveUsersMode = formData?.massiveUsersMode

   const getGroups = useCallback(
      debounce(query => {
         setGroupsStatus(true)
         getAllActiveGroupsApi(query)
            .then(response => setGroups(response?.filter(group => group?.status == 'A')))
            .finally(() => setGroupsStatus(false))
      }, 500),
      [setGroups, setGroupsStatus]
   )

   const handleUserSearch = debounce(e => {
      if (isMassiveUsersMode) {
         const splittedEmails = UTILS.splitTextUsers(e.target.value)
         setForm('usersId', [...new Set([...formData.usersId, ...splittedEmails])])
      } else setUserToSearch(e.target.value)
   }, DELAY_REQUEST)

   const handleSelectUsers = (event, value, reason) => {
      setForm('usersId', value)
   }

   const handleUsersMode = async (_, value) => {
      reset()
      setForm('massiveUsersMode', value)
      setUserListFiltered([])
   }

   const getUserListByNameOrEmail = async param => {
      setRequestLoading(true)
      let usersList = []
      await getFilteredUSersApi(param).then(response => {
         usersList = response
         setUserListFiltered(usersList)
         setRequestLoading(false)
      })
   }

   const isValidForm = () => {
      const isInputError = validateForm()
      for (const prop in isInputError) {
         if (isInputError[prop]) return false
      }
      return true
   }

   const saveGroupToRolesAndUsers = () => {
      if (isValidForm()) {
         assignGroupToRolesAndUsers(formData)
      }
   }

   const validateForm = () => {
      let result = { ...isInputError }
      // if (isInputError.groupId || isInputError.usersId) {
      const userIdErrorMessage = UTILS.isMassiveUserErrorMessage(isMassiveUsersMode, formData)
      result = {
         ...isInputError,
         groupId: !formData.groupId?.length,
         usersId: userIdErrorMessage
      }
      // }
      setIsInputError(result)
      return result
   }

   useEffect(() => getGroups(groupsQuery), [groupsQuery])

   useEffect(() => {
      renders >= 3 && validateForm()
      setData(formData)
      renders++
   }, [formData])

   useEffect(() => {
      setData({...data, usersId: []})
      setForm('usersId', [])
   }, [formData.massiveUsersMode])

   useEffect(() => {
      if (userToSearch.trim().length && !isMassiveUsersMode) {
         getUserListByNameOrEmail(userToSearch)
      }
   }, [userToSearch])

   return (
      <Grid className={classes.formContainer}>
         <Typography className={classes.titleOne}>
            <b>Completa los campos</b>
         </Typography>
         <form className={classes.formContainer}>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
               <Autocomplete
                  id="groupId"
                  name="groupId"
                  value={formData?.groupId}
                  options={groups}
                  loading={areGroupsLoading}
                  multiple
                  getOptionSelected={(option, value) => option.id === value.id}
                  getOptionLabel={option => option?.name || ''}
                  onChange={(event, value) => {
                     event.persist()
                     setForm('groupId', value)
                  }}
                  size="small"
                  renderOption={item => (
                     <Grid container>
                        <Typography>{item?.name}</Typography>
                        <Grid container className={classes.groupOptionItem}>
                           {item?.roles?.map(rol => (
                              <Typography component="div" variant="caption" key={rol?.id}>
                                 {rol?.name}
                              </Typography>
                           ))}
                        </Grid>
                     </Grid>
                  )}
                  renderInput={params => (
                     <TextField
                        {...params}
                        InputProps={{
                           ...params.InputProps,
                           endAdornment: (
                              <React.Fragment>
                                 {areGroupsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                 {params.InputProps.endAdornment}
                              </React.Fragment>
                           )
                        }}
                        label="Grupo(s)"
                        variant="outlined"
                        onChange={e => setGroupsQuery(e?.target?.value)}
                        error={isInputError.groupId}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>
                  {isInputError.groupId && TRANSLATE.FORM.SELECT_VALID_GROUP}
               </FormHelperText>
            </FormControl>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
               <Autocomplete
                  multiple
                  limitTags={5}
                  disableCloseOnSelect
                  id="entity-select"
                  style={{ width: '100%' }}
                  options={userListFiltered}
                  value={formData.usersId}
                  loading={requestLoading}
                  onChange={handleSelectUsers}
                  filterOptions={filterOptions}
                  getOptionLabel={option => option.email}
                  getOptionSelected={(option, value) => option.id === value.id}
                  renderOption={(option, { selected }) => (
                     <>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        <div>
                           <p className={classes.nameTitleCheckbox}>{option.email}</p>
                           <p className={classes.nameSubTitleCheckbox}>
                              {option.name} {option.lastName}
                           </p>
                        </div>
                     </>
                  )}
                  noOptionsText={
                     isMassiveUsersMode
                        ? 'Pegue listado de usuarios'
                        : formData.usersId
                        ? 'Escriba un criterio de búsqueda'
                        : 'Sin resultados'
                  }
                  renderTags={UTILS.RenderTags}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label="Seleccionar un usuario"
                        name="usersId"
                        variant="outlined"
                        size="small"
                        inputProps={{
                           ...params.inputProps,
                           autoComplete: 'off',
                           endAdornment: (
                              <React.Fragment>
                                 {requestLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                 {params.InputProps.endAdornment}
                              </React.Fragment>
                           )
                        }}
                        value={userToSearch}
                        onChange={handleUserSearch}
                        error={Boolean(isInputError.usersId)}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>{isInputError.usersId}</FormHelperText>
            </FormControl>
            <FormControl className={classes.formControl}>
               <Grid container className={classes.massiveAssignCheckbox}>
                  <FormControlLabel
                     control={
                        <Checkbox
                           color="primary"
                           name="massiveUsersMode"
                           checked={formData.massiveUsersMode}
                           onChange={handleUsersMode}
                        />
                     }
                     label="Ingresar usuarios masivamente"
                  />
                  <UTILS.MassiveAssignHelper />
               </Grid>
            </FormControl>
            <Button
               fullWidth
               variant="contained"
               color="primary"
               disableElevation
               className={classes.button}
               onClick={saveGroupToRolesAndUsers}
               disabled={!RELACIONAR_GRUPO}
            >
               {TRANSLATE.BUTTONS.SAVE}
            </Button>
         </form>
      </Grid>
   )
}

export default GroupToRolesAndUsersForm
